<template>
  <article
    class="product-view-grid"
    :class="{ 'product-view-grid--web-masked': isFullyWebMasked }"
  >
    <div class="product-cover-area">
      <img
        :src="coverUrl"
        :alt="`${product.title || 'Product'} cover`"
        class="product-cover"
        loading="lazy"
        @error="useDefaultCover"
      />
      <a
        v-if="isFullyWebMasked && webMaskedHelpUrl"
        :href="webMaskedHelpUrl"
        class="web-masked-indicator"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="webMaskedStatusMessage"
        :title="webMaskedStatusMessage"
      >
        <v-icon small>mdi-eye-off-outline</v-icon>
      </a>
      <span
        v-else-if="isFullyWebMasked"
        class="web-masked-indicator"
        role="img"
        :aria-label="webMaskedStatusMessage"
        :title="webMaskedStatusMessage"
      >
        <v-icon small>mdi-eye-off-outline</v-icon>
      </span>
    </div>

    <div class="product-body">
      <div class="product-body-header">
        <h2 class="product-title">
          <a
            v-if="productShareUrl"
            :href="productShareUrl"
            class="product-title-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ product.title || "Untitled" }}
          </a>
          <template v-else>{{ product.title || "Untitled" }}</template>
        </h2>
        <div v-if="$slots['actions-before']" class="product-body-featured">
          <slot name="actions-before" />
        </div>
      </div>
      <p v-if="product.subtitle" class="product-subtitle">
        {{ product.subtitle }}
      </p>
      <p v-if="composerArranger" class="product-credits">
        {{ composerArranger }}
      </p>
      <span v-if="gradeLabel" class="product-grade">{{ gradeLabel }}</span>
    </div>

    <div class="product-card-actions">
      <button
        v-if="showPreviewMedia"
        type="button"
        class="product-card-action"
        :aria-label="`${previewMediaDisplay.label} preview for ${
          product.title || 'product'
        }`"
        :title="previewMediaDisplay.label"
        @click="openExternalUrl(previewMediaUrl)"
      >
        <img
          :src="previewMediaDisplay.icon"
          :class="previewMediaDisplay.className"
          :alt="previewMediaDisplay.alt"
          aria-hidden="true"
        />
        <span>{{ previewMediaDisplay.label }}</span>
      </button>

      <button
        v-if="videoUrl"
        type="button"
        class="product-card-action"
        :aria-label="`Video for ${product.title || 'product'}`"
        title="Video"
        @click="openExternalUrl(videoUrl)"
      >
        <img :src="videoIcon" class="video-icon" alt="" aria-hidden="true" />
        <span>Video</span>
      </button>

      <router-link
        v-if="productId"
        :to="editProductRoute"
        class="product-card-action"
        :aria-label="`Edit ${product.title || 'product'}`"
        title="Edit product"
      >
        <svg class="edit-product-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
          <path d="m13.5 6.5 4 4" />
        </svg>
        <span>Edit</span>
      </router-link>

      <button
        v-if="firstSkuId"
        type="button"
        class="product-card-action"
        :disabled="isFullyWebMasked"
        :aria-disabled="isFullyWebMasked ? 'true' : undefined"
        :aria-label="
          isFullyWebMasked
            ? webMaskedStatusMessage
            : `Copy link for ${product.title || 'product'}`
        "
        :title="
          isFullyWebMasked ? webMaskedStatusMessage : 'Copy product link'
        "
        @click="copyProductLink"
      >
        <svg
          class="share-product-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="26"
          fill="currentColor"
          stroke="currentColor"
          viewBox="0 0 22 26"
          aria-hidden="true"
        >
          <path
            d="M7.66667 12.3333C7.66667 13.2174 7.31548 14.0652 6.69036 14.6904C6.06523 15.3155 5.21739 15.6667 4.33333 15.6667C3.44928 15.6667 2.60143 15.3155 1.97631 14.6904C1.35119 14.0652 1 13.2174 1 12.3333C1 11.4493 1.35119 10.6014 1.97631 9.97631C2.60143 9.35119 3.44928 9 4.33333 9C5.21739 9 6.06523 9.35119 6.69036 9.97631C7.31548 10.6014 7.66667 11.4493 7.66667 12.3333Z"
            stroke-width="1.25"
          />
          <path
            d="M14.76 19.404L7.66669 14.7213M14.8934 6.12134L7.80002 10.8013"
            stroke-width="1.25"
            stroke-linecap="round"
          />
          <path
            d="M21 21.6667C21 22.5507 20.6488 23.3986 20.0237 24.0237C19.3985 24.6488 18.5507 25 17.6666 25C16.7826 25 15.9347 24.6488 15.3096 24.0237C14.6845 23.3986 14.3333 22.5507 14.3333 21.6667C14.3333 20.7826 14.6845 19.9348 15.3096 19.3096C15.9347 18.6845 16.7826 18.3333 17.6666 18.3333C18.5507 18.3333 19.3985 18.6845 20.0237 19.3096C20.6488 19.9348 21 20.7826 21 21.6667ZM21 4.33333C21 5.21739 20.6488 6.06523 20.0237 6.69036C19.3985 7.31548 18.5507 7.66667 17.6666 7.66667C16.7826 7.66667 15.9347 7.31548 15.3096 6.69036C14.6845 6.06523 14.3333 5.21739 14.3333 4.33333C14.3333 3.44928 14.6845 2.60143 15.3096 1.97631C15.9347 1.35119 16.7826 1 17.6666 1C18.5507 1 19.3985 1.35119 20.0237 1.97631C20.6488 2.60143 21 3.44928 21 4.33333Z"
            stroke-width="1.25"
          />
        </svg>
        <span>Copy link</span>
      </button>
    </div>

    <div
      v-if="showCopiedAlert"
      class="copy-product-alert"
      role="status"
      aria-live="polite"
    >
      <span class="copy-product-alert-text"
        >Product link copied to your clipboard.</span
      >
    </div>
  </article>
</template>

<script>
import { JWP_PREVIEW_MEDIA_URL } from "./constants.js";
import manifestCoverMixin from "./manifestCoverMixin.js";
import audioPreviewIcon from "./assets/audio-preview-icon.svg";
import previewMediaIcon from "./assets/preview-media-icon.svg";
import scorePreviewIcon from "./assets/score-preview-icon.svg";
import videoIcon from "./assets/video-icon.svg";

const WEB_MASKED_STATUS_MESSAGE =
  "This product is not currently visible on jwpepper.com.";

export default {
  name: "ProductViewGrid",
  mixins: [manifestCoverMixin],

  props: {
    product: {
      type: Object,
      required: true,
    },
    productCodes: {
      type: Object,
      default: null,
    },
    webMaskedHelpUrl: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      audioPreviewIcon,
      copiedAlertTimer: null,
      previewMediaIcon,
      scorePreviewIcon,
      showCopiedAlert: false,
      videoIcon,
    };
  },

  computed: {
    productId() {
      return String(
        this.product.productId ||
          this.product.productGroupId ||
          this.product.skus?.[0]?.skuId ||
          "",
      );
    },

    firstSku() {
      return this.product.skus?.[0] || null;
    },

    firstSkuId() {
      return String(this.firstSku?.skuId || "");
    },

    isFullyWebMasked() {
      const skus = this.product && this.product.skus;
      if (!Array.isArray(skus) || skus.length === 0) {
        return false;
      }
      return skus.every((sku) => sku && sku.webStatus === "M");
    },

    webMaskedStatusMessage() {
      return WEB_MASKED_STATUS_MESSAGE;
    },

    productShareUrl() {
      if (this.isFullyWebMasked || !this.firstSkuId) {
        return "";
      }

      return `https://www.jwpepper.com/${this.firstSkuId}.item`;
    },

    editProductRoute() {
      return {
        name: "ProductEdit",
        params: { productId: this.productId },
      };
    },

    composerIncludesArranger() {
      const composer = String(this.product.composer || "").toLowerCase();
      const arranger = String(this.product.arranger || "").toLowerCase();
      return Boolean(arranger && composer.includes(arranger));
    },

    composerArranger() {
      const composer = String(this.product.composer || "").trim();
      const arranger = String(this.product.arranger || "").trim();

      if (composer && arranger && !this.composerIncludesArranger) {
        return `${composer}/arr. ${arranger}`;
      }
      if (composer) {
        return composer;
      }
      if (arranger) {
        return `arr. ${arranger}`;
      }
      return "";
    },

    gradeLabel() {
      return this.getGrade(this.firstSku);
    },

    showPreviewMedia() {
      return this.hasPreviewMedia(this.firstSku);
    },

    previewMediaDisplay() {
      return this.getPreviewMediaDisplay(this.firstSku);
    },

    previewMediaUrl() {
      return this.getPreviewMediaUrl(this.firstSku);
    },

    videoUrl() {
      return this.getVideoUrl(this.firstSku);
    },
  },

  watch: {
    product() {
      this.showCopiedAlert = false;
    },
  },

  methods: {
    async copyProductLink() {
      if (this.isFullyWebMasked || !this.productShareUrl) {
        return;
      }

      try {
        if (
          typeof navigator !== "undefined" &&
          navigator.clipboard?.writeText
        ) {
          await navigator.clipboard.writeText(this.productShareUrl);
          this.showProductCopiedAlert();
          return;
        }
      } catch (_error) {
        // Fall through to execCommand when the Clipboard API is unavailable.
      }

      if (this.copyTextWithFallback(this.productShareUrl)) {
        this.showProductCopiedAlert();
      }
    },

    copyTextWithFallback(value) {
      if (typeof document === "undefined") {
        return false;
      }

      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = Boolean(document.execCommand("copy"));
      } catch (_error) {
        copied = false;
      } finally {
        document.body.removeChild(textarea);
      }

      return copied;
    },

    showProductCopiedAlert() {
      this.showCopiedAlert = true;
      if (this.copiedAlertTimer) {
        window.clearTimeout(this.copiedAlertTimer);
      }
      this.copiedAlertTimer = window.setTimeout(() => {
        this.showCopiedAlert = false;
        this.copiedAlertTimer = null;
      }, 2400);
    },

    getGrade(sku) {
      if (!Array.isArray(sku?.grades) || !sku.grades.length) {
        return "";
      }

      return sku.grades
        .map((grade) => grade.description || grade.code)
        .filter(Boolean)
        .join(", ");
    },

    hasScore(sku) {
      return Boolean(
        sku?.scoreAvailable ||
        sku?.hasScore ||
        sku?.previewMedia?.scoreAvailable,
      );
    },

    hasAudio(sku) {
      return Boolean(
        sku?.audioAvailable ||
        sku?.hasAudio ||
        sku?.previewMedia?.audioAvailable,
      );
    },

    hasPreviewMedia(sku) {
      return this.hasScore(sku) || this.hasAudio(sku);
    },

    getPreviewMediaDisplay(sku) {
      if (this.hasScore(sku) && this.hasAudio(sku)) {
        return {
          icon: this.previewMediaIcon,
          alt: "",
          label: "Score/Audio",
          className: "preview-media-icon",
        };
      }

      if (this.hasAudio(sku)) {
        return {
          icon: this.audioPreviewIcon,
          alt: "",
          label: "Audio",
          className: "audio-preview-icon",
        };
      }

      return {
        icon: this.scorePreviewIcon,
        alt: "",
        label: "Score",
        className: "score-preview-icon",
      };
    },

    getPreviewMediaUrl(sku) {
      const productId = encodeURIComponent(String(sku?.skuId || ""));
      return `${JWP_PREVIEW_MEDIA_URL}?type=audio&productID=${productId}`;
    },

    getVideoUrl(sku) {
      return sku?.youtubeUrl || "";
    },

    openExternalUrl(url) {
      if (!url || typeof window === "undefined") {
        return;
      }

      try {
        const parsed = new URL(String(url), window.location.href);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return;
        }
        window.open(parsed.toString(), "_blank", "noopener,noreferrer");
      } catch (_error) {
        // Ignore invalid URLs
      }
    },
  },

  beforeDestroy() {
    if (this.copiedAlertTimer) {
      window.clearTimeout(this.copiedAlertTimer);
    }
  },
};
</script>

<style scoped>
.product-view-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 0.25rem;
  color: #252525;
  background: #fff;
  box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.075);
}

.product-cover-area {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: 0.85rem 0.85rem 0.35rem;
  background: #f6f4f5;
}

.product-cover {
  display: block;
  max-width: 72%;
  max-height: 100%;
  object-fit: contain;
  background: #fff;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.12);
}

.web-masked-indicator {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  color: #424242;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  text-decoration: none;
}

.web-masked-indicator:hover,
.web-masked-indicator:focus {
  color: #173d50;
}

.web-masked-indicator:focus-visible {
  outline: 2px solid #35647b;
  outline-offset: 2px;
}

.product-view-grid--web-masked .product-cover,
.product-view-grid--web-masked .product-body,
.product-view-grid--web-masked .product-card-actions {
  opacity: 0.55;
}

.product-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  min-height: 0;
  padding: 0.9rem 1rem 0.35rem;
}

.product-body-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.25rem;
  width: 100%;
}

.product-body-featured {
  flex: 0 0 auto;
  margin: -0.35rem -0.4rem 0 0;
}

.product-body-featured >>> .featured-star-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
}

.product-body-featured >>> .featured-star-label {
  display: none;
}

.product-title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: #1c1c1c;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.product-title-link {
  color: inherit;
  text-decoration: none;
  word-break: break-word;
}

.product-title-link:hover,
.product-title-link:focus {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}

.product-subtitle {
  margin: 0.3rem 0 0;
  color: #1c1c1c;
  font-size: 0.875rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.product-credits {
  margin: 0.35rem 0 0;
  color: #717584;
  font-size: 0.8125rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.product-grade {
  display: inline-block;
  margin-top: 0.55rem;
  padding: 0.15rem 0.55rem;
  border-radius: 0.25rem;
  color: #c45c12;
  background: #f8ead6;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
}

.product-card-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-evenly;
  gap: 0.35rem 0.15rem;
  margin-top: auto;
  padding: 0.85rem 0.5rem 1rem;
}

.product-card-action {
  display: inline-flex;
  flex: 1 1 3.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  min-width: 3.25rem;
  max-width: 5.5rem;
  padding: 0.15rem;
  border: 0;
  border-radius: 2px;
  color: #252525;
  background: transparent;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

.product-card-action:hover,
.product-card-action:focus {
  opacity: 0.72;
}

.product-card-action:disabled {
  cursor: not-allowed;
}

.product-card-action:disabled:hover,
.product-card-action:disabled:focus {
  opacity: 1;
}

.product-card-action:focus-visible {
  outline: 2px solid #35647b;
  outline-offset: 3px;
}

.edit-product-icon {
  width: 1.45rem;
  height: 1.45rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.share-product-icon {
  width: 1.2rem;
  height: 1.45rem;
}

.preview-media-icon {
  width: 31px;
  height: 32px;
}

.audio-preview-icon {
  width: 30px;
  height: 26px;
  margin-top: 3px;
  margin-bottom: 3px;
}

.score-preview-icon {
  width: 24px;
  height: 29px;
  margin-top: 1.5px;
  margin-bottom: 1.5px;
}

.video-icon {
  width: 30px;
  height: 20px;
  margin-top: 6px;
  margin-bottom: 6px;
}

.copy-product-alert {
  position: fixed;
  bottom: 30px;
  left: 30px;
  z-index: 1000;
  width: min(400px, calc(100vw - 60px));
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: 8px;
  color: #fff;
  background: var(--product-copy-alert-background, #133e4f);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  text-align: left;
}

.copy-product-alert-text {
  display: inline-block;
  font-size: 22px;
  padding: 8px 16px;
}
</style>
